import { createEntityAdapter, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import client from "../../utils/axios";

const adapter = createEntityAdapter<Category>();
const slice = createSlice({
  name: "categories",
  initialState: adapter.getInitialState({
    status: "idle" as LoadingStatus,
    total: 0,
    pagesCount: 1,
    currentPage: null as number | null,
  }),
  reducers: {
    setStatus(state, { payload }: PayloadAction<LoadingStatus>) {
      state.status = payload;
    },
    hasError(state, action) {
      state.status = "failed";
    },
    getCategoriesSuccess(state, action: PayloadAction<ResultList<Category>>) {
      const { meta, data } = action.payload;
      state.status = "success";
      state.total = meta.total;
      state.pagesCount = meta.last_page;
      state.currentPage = meta.current_page;
      adapter.setAll(state, data);
    },
    addCategorySuccess(state, action: PayloadAction<Result<Category>>) {
      state.status = "success";
      adapter.upsertOne(state, action.payload.data);
    },
    deleteCategorySuccess(state, action: PayloadAction<number>) {
      adapter.removeOne(state, action.payload);
    },
  },
});

const { actions, reducer } = slice;

const { selectAll } = adapter.getSelectors();
export const selectCategories = createSelector(
  (state: RootState) => state.categories,
  (categories) => selectAll(categories)
);

export const getCategories: AsyncAction = (page: number) => {
  return async (dispatch, getState) => {
    if (getState().categories.currentPage === page) return;

    dispatch(actions.setStatus("pending"));
    try {
      const res = await client.get<any, ResultList<Category>>("/categories", { params: { page } });
      return dispatch(actions.getCategoriesSuccess(res));
    } catch (err) {
      throw dispatch(actions.hasError(err));
    }
  };
};

export const showCategory: AsyncAction = (id: number) => {
  return async (dispatch, getState) => {
    if (getState().categories.entities[id] !== undefined) return;

    dispatch(actions.setStatus("pending"));
    try {
      const res = await client.get<any, Result<Category>>(`/categories/${id}`);
      return dispatch(actions.addCategorySuccess(res));
    } catch (err) {
      throw dispatch(actions.hasError(err));
    }
  };
};

export const storeCategory: AsyncAction = (data: StoreCategory) => {
  return async (dispatch) => {
    dispatch(actions.setStatus("mutating"));
    try {
      const res = await client.post<any, Result<Category>>("/categories/", data);
      return dispatch(actions.addCategorySuccess(res));
    } catch (err) {
      throw dispatch(actions.hasError(err));
    }
  };
};

export const updateCategory: AsyncAction = ({ id, ...patch }: StoreCategory & Pick<Category, "id">) => {
  return async (dispatch) => {
    dispatch(actions.setStatus("mutating"));
    try {
      const res = await client.put<any, Result<Category>>(`/categories/${id}`, patch);
      return dispatch(actions.addCategorySuccess(res));
    } catch (err) {
      throw dispatch(actions.hasError(err));
    }
  };
};

export const deleteCategory: AsyncAction = (categoryId: number) => {
  return async (dispatch, getState) => {
    const item = getState().categories.entities[categoryId];
    if (item === undefined) return;

    dispatch(actions.deleteCategorySuccess(item.id));
    try {
      return await client.delete<any, { message: string }>(`/categories/${categoryId}`);
    } catch (err) {
      dispatch(actions.addCategorySuccess({ data: item })); // put it pack instead of errors on deleting
      throw dispatch(actions.hasError(err));
    }
  };
};

export default reducer;
